// src/pages/competition/CompetitionPortalHome.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Activity, Award, ArrowRight, Users, Sparkles, Zap, Lock, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import GlowingCard from '../../components/ui/GlowingCard';
import { useTheme } from '../../context/ThemeContext';
import CompanyLogo from '../../components/CompanyLogo';
import ddLogo from '../../components/ui/dd.png';
import { validateTabulatorPassword, storeTabulatorAuth } from '../../config/passwordConfig';

// Updated to use original DD logo with glow effect
const CompetitionPortalHome = () => {
  const [role, setRole] = useState(null);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Password protection state for tabulator access
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleContinue = () => {
    if (role === 'tabulator') {
      // Show password modal for tabulator access
      setShowPasswordModal(true);
    } else if (role === 'judge') {
      navigate('/competition/setup', { state: { selectedRole: 'judge' } });
    }
  };

  // Handle password verification for tabulator access
  const handlePasswordSubmit = () => {
    if (validateTabulatorPassword(password)) {
      // Store tabulator authorization in session
      storeTabulatorAuth();
      
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError('');
      
      // Check if there's a return URL from direct access attempt
      const returnUrl = routerLocation.state?.returnUrl;
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate('/competition/setup', { state: { selectedRole: 'tabulator' } });
      }
    } else {
      setPasswordError('Incorrect password. Please contact your administrator.');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Stage Background - similar to homepage */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-pink-900/20"></div>
      
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-pink-500/30 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>
      
      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="max-w-2xl w-full"
        >
          {/* Header with logo */}
          <div className="text-center mb-8">
      <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-6"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="relative"
              >
                <img 
                  src={ddLogo}
                  alt="Dance Digital"
                  className="w-20 h-20 object-contain relative z-10"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl blur-xl opacity-60"></div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/40 text-base text-pink-200 mb-8 backdrop-blur-xl"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              <span className="font-semibold">Live Competition System</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 whitespace-nowrap"
            >
              <span className="text-white drop-shadow-2xl">COMPETITION </span>
              <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent drop-shadow-2xl">PORTAL</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-xl text-white/80 font-medium leading-relaxed mb-12 max-w-2xl mx-auto"
            >
              Enter the future of dance competitions. Choose your role to access 
              <span className="text-pink-300 font-bold"> real-time scoring</span>, 
              <span className="text-purple-300 font-bold"> live analytics</span>, and 
              <span className="text-orange-300 font-bold"> seamless management</span>.
            </motion.p>
          </div>
          
          {/* Role selection cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="grid md:grid-cols-2 gap-6 mb-8"
          >
            {/* Tabulator Card */}
            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setRole('tabulator')}
              className={`relative group cursor-pointer transition-all duration-300 ${
                role === 'tabulator' ? 'z-10' : ''
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r from-blue-500/30 to-indigo-500/30 rounded-2xl blur-xl transition-opacity duration-300 ${
                role === 'tabulator' ? 'opacity-60' : 'opacity-0 group-hover:opacity-40'
              }`}></div>
              
              <div className={`relative bg-white/5 backdrop-blur-xl rounded-2xl border transition-all duration-300 p-8 min-h-[340px] ${
                role === 'tabulator'
                  ? 'border-blue-400/50 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 shadow-2xl shadow-blue-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}>
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    role === 'tabulator'
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25'
                      : 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-400/30'
                  }`}>
                    <Activity className={`w-8 h-8 ${
                      role === 'tabulator' ? 'text-white' : 'text-blue-400'
                    }`} />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-3">Tabulator</h3>
                  <p className="text-white/70 mb-6 leading-relaxed">
                    Manage competitions, control scoring flow, and oversee the entire event experience.
                  </p>
                  
                  <div className="space-y-2 text-left">
                    <div className="flex items-center text-sm text-white/60">
                      <Zap className="w-4 h-4 mr-2 text-blue-400" />
                      Real-time score monitoring
                    </div>
                    <div className="flex items-center text-sm text-white/60">
                      <Users className="w-4 h-4 mr-2 text-blue-400" />
                      Judge coordination
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Judge Card */}
            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setRole('judge')}
              className={`relative group cursor-pointer transition-all duration-300 ${
                role === 'judge' ? 'z-10' : ''
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-2xl blur-xl transition-opacity duration-300 ${
                role === 'judge' ? 'opacity-60' : 'opacity-0 group-hover:opacity-40'
              }`}></div>
              
              <div className={`relative bg-white/5 backdrop-blur-xl rounded-2xl border transition-all duration-300 p-8 min-h-[340px] ${
                role === 'judge'
                  ? 'border-purple-400/50 bg-gradient-to-br from-purple-500/10 to-pink-500/10 shadow-2xl shadow-purple-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}>
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    role === 'judge'
                      ? 'bg-gradient-to-br from-purple-500 to-pink-600 shadow-lg shadow-purple-500/25'
                      : 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-400/30'
                  }`}>
                    <Award className={`w-8 h-8 ${
                      role === 'judge' ? 'text-white' : 'text-purple-400'
                    }`} />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-3">Judge</h3>
                  <p className="text-white/70 mb-6 leading-relaxed">
                    Score performances with precision using our intuitive judging interface.
                  </p>
                  
                  <div className="space-y-2 text-left">
                    <div className="flex items-center text-sm text-white/60">
                      <Sparkles className="w-4 h-4 mr-2 text-purple-400" />
                      Advanced scoring system
                    </div>
                    <div className="flex items-center text-sm text-white/60">
                      <Zap className="w-4 h-4 mr-2 text-purple-400" />
                      Instant score submission
                    </div>
                  </div>
          </div>
        </div>
      </motion.div>
          </motion.div>
          
          {/* Continue button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-center"
          >
            <AnimatePresence>
              {role && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="group inline-block"
                >
                  <button
                    onClick={handleContinue}
                    className="px-12 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 flex items-center space-x-3 text-lg font-bold border border-pink-400/50 relative overflow-hidden mx-auto"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                    <span>Continue as {role === 'tabulator' ? 'Tabulator' : 'Judge'}</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>

      {/* Password Modal for Tabulator Access */}
      {showPasswordModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Tabulator Access</h3>
              <p className="text-white/70">Enter the tabulator password to continue</p>
            </div>
            
            <div className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-blue-400/50 focus:bg-white/15 transition-all pr-12"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {passwordError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-sm text-center"
                >
                  {passwordError}
                </motion.p>
              )}
              
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setPasswordError('');
                    setRole(null);
                  }}
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all font-semibold"
                >
                  Continue
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default CompetitionPortalHome;